import { isFromBreakpoint, isUntilBreakpoint } from '~/breakpoints';
import { classes, debounce } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

/** @typedef {String|HTMLElement} Dropdown */

customElements.define(
  'site-nav',
  class SiteNav extends HTMLCustomElement {
    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      this.on('resize', window, debounce(this.handleResize.bind(this)));

      if (this.targets.parent) this.on('click', this.targets.parent.all, this.handleParentClick.bind(this));
      if (this.targets.return) this.on('click', this.targets.return.all, this.handleReturnClick.bind(this));

      if (this.targets['dropdown-container']) {
        this.on('mouseenter', this.targets['dropdown-container'].all, this.handleDropdownMouseEnter.bind(this));
        this.on('mouseleave', this.targets['dropdown-container'].all, this.handleDropdownMouseLeave.bind(this));
      }
    }

    /**
     * Handles the window resize.
     */
    handleResize() {
      this.isMedium = isFromBreakpoint('medium');
    }

    /**
     * Handles the parent hover event.
     * @param {MouseEvent} event - The event payload.
     */
    handleDropdownMouseEnter(event) {
      if (isFromBreakpoint('medium') && Number(event.target.dataset.tier) === 1) {
        this.openDropdown(
          event.target.dataset.parent,
          !!(event.fromElement ?? event.relatedTarget)?.closest('[data-target="site-nav.dropdown-container"]')
        );
      }
    }

    /**
     * Handles the parent hover out event.
     * @param {MouseEvent} event - The event payload.
     */
    handleDropdownMouseLeave(event) {
      if (isFromBreakpoint('medium') && Number(event.target.dataset.tier) === 1) {
        const target = event.toElement ?? event.relatedTarget;

        if (!target) return;

        this.closeDropdown(
          event.target.dataset.parent,
          !!target.closest('[data-target="site-nav.dropdown-container"]')
        );
      }
    }

    /**
     * Handles a parent item click event.
     * @param {MouseEvent} event - The event payload.
     */
    handleParentClick(event) {
      if (isFromBreakpoint('medium')) return;

      const dropdown = this.getDropdown(event.currentTarget.id);

      event.preventDefault();

      if (!dropdown) return;
      if (!dropdown.classList.contains(classes.open)) return this.openDropdown(dropdown);

      this.closeDropdown(dropdown);
    }

    /**
     * Handles the return button click.
     * @param {MouseEvent} event - The event payload.
     */
    handleReturnClick(event) {
      this.closeDropdown(event.currentTarget.dataset.parent);
    }

    /**
     * Opens the given dropdown.
     * @param {Dropdown} dropdown
     * @param {Boolean} immediate - Opens without a transition.
     */
    openDropdown(dropdown, immediate) {
      const element = this.getDropdown(dropdown);
      const transitionDuration = getComputedStyle(element).transitionDuration;
      const parent = this.targets.parent.all.find(({ id }) => id === element.dataset.parent);

      if (immediate) {
        element.style.transitionDuration = '0s';

        setTimeout(() => {
          element.style.transitionDuration = transitionDuration;
        }, parseFloat(transitionDuration) * 1000);
      }

      if (element) element.classList.add(classes.open);
      if (parent) parent.classList.add(classes.open);
    }

    /**
     * Closes the given dropdown.
     * @param {Dropdown} dropdown
     * @param {Boolean} immediate - Opens without a transition.
     */
    closeDropdown(dropdown, immediate) {
      const element = this.getDropdown(dropdown);
      const transitionDuration = getComputedStyle(element).transitionDuration;
      const parent = this.targets.parent.all.find(({ id }) => id === element.dataset.parent);

      if (immediate) {
        element.style.transitionDuration = '0s';

        setTimeout(() => {
          element.style.transitionDuration = transitionDuration;
        }, parseFloat(transitionDuration) * 1000);
      }

      if (element) element.classList.remove(classes.open);
      if (parent) parent.classList.remove(classes.open);
    }

    /**
     * Returns a dropdown element.
     * @param {Dropdown} dropdown
     */
    getDropdown(dropdown) {
      return typeof dropdown === 'string'
        ? this.targets.dropdown && this.targets.dropdown.all.find((element) => element.dataset.parent === dropdown)
        : dropdown;
    }

    /**
     * Handles the medium breakpoint state.
     */
    #isMedium = isFromBreakpoint('medium');

    get isMedium() {
      return this.#isMedium;
    }

    set isMedium(value) {
      if (value === this.#isMedium) return;

      this.#isMedium = value;

      /**
       * Close all dropdowns.
       */
      this.targets.dropdown.all.forEach(this.closeDropdown.bind(this));
    }
  }
);
